import axios from 'axios';
import apiClient from '../apiClient';
import type { BaseResponse } from '../../types/base.dto';

export interface OSSUploadToken {
  accessKeyId: string;
  host: string;
  dir: string;
  policy: string;
  signature: string;
  expireAt: number;
  maxSizeMB: number;
}

const getRandomString = (length: number) => {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const buffer = new Uint8Array(length);
  crypto.getRandomValues(buffer);
  return Array.from(buffer)
    .map((b) => charset[b % charset.length])
    .join('');
};

const buildObjectKey = (dir: string, filename: string) => {
  const safeDir = dir ? dir.replace(/^\/+/, '').replace(/\/+$/, '') + '/' : '';
  const ext = filename.includes('.') ? `.${filename.split('.').pop()}` : '';
  const key = `${safeDir}${Date.now()}_${getRandomString(16)}${ext}`;
  return key.replace(/\/{2,}/g, '/');
};

export const cloudApi = {
  getOSSUploadToken: async (dir?: string): Promise<OSSUploadToken> => {
    const response = await apiClient.post<BaseResponse<OSSUploadToken>>('/cloud/oss/upload-token', {
      dir: dir || '',
    });
    return response.data.data;
  },

  uploadToOSS: async (
    file: File,
    token: OSSUploadToken,
    objectKey?: string,
    onProgress?: (percent: number) => void
  ): Promise<{ url: string; objectKey: string }> => {
    const key = objectKey || buildObjectKey(token.dir, file.name);
    const formData = new FormData();
    formData.append('key', key);
    formData.append('policy', token.policy);
    formData.append('OSSAccessKeyId', token.accessKeyId);
    formData.append('Signature', token.signature);
    formData.append('success_action_status', '200');
    formData.append('file', file);

    await axios.post(token.host, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000,
      onUploadProgress: (evt) => {
        if (!onProgress) return;
        if (!evt.total) return;
        const percent = Math.round((evt.loaded / evt.total) * 100);
        onProgress(percent);
      },
    });

    return { url: `${token.host.replace(/\/+$/, '')}/${key}`, objectKey: key };
  },
};

export default cloudApi;
